import React, { createContext, useContext, ReactNode } from 'react';

interface FeatureFlagData {
  enabled: boolean;
  metadata: Record<string, any>;
}

interface FeatureFlagContextType {
  flags: Record<string, FeatureFlagData>;
  loading: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

/**
 * Simplified FeatureFlagProvider - returns fixed values without network calls.
 * The inline_checkout_enabled flag is now always enabled (100% rollout).
 * This improves page load performance and eliminates race conditions.
 */
export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  // Fixed values - no network call needed
  const flags: Record<string, FeatureFlagData> = {
    'inline_checkout_enabled': { enabled: true, metadata: {} }
  };
  const loading = false;

  return (
    <FeatureFlagContext.Provider value={{ flags, loading }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
  }
  return context;
};

// Helper function to clear cache (kept for backwards compatibility)
export const clearFeatureFlagCache = (flagName?: string) => {
  const STORAGE_PREFIX = 'feature_flag_';
  if (flagName) {
    localStorage.removeItem(`${STORAGE_PREFIX}${flagName}`);
  } else {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }
};
